import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { calculatePricing, getAvailableSymbols, listPricingPlans, logPricingEvent } from '../../api/rlApi';
import CTAButton from '../common/CTAButton';

const formatPrice = (value, currency = 'USD') => {
  if (value === null || value === undefined) return '—';
  if (Number(value) <= 0) return 'Get a quote';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch (e) {
    return `$${Number(value).toFixed(0)}`;
  }
};

// For tooling line items, show $0 instead of "Get a quote" when zero.
const formatTooling = (value) => {
  if (value === null || value === undefined) return '—';
  if (Number(value) === 0) return '$0';
  return formatPrice(value);
};

const formatUnit = (value) => {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString();
};

const faqItems = [
  {
    q: 'Can I switch plans later?',
    a: 'Yes. You can upgrade or downgrade any time; discounts prorate on the next cycle.',
  },
  {
    q: 'Do you offer pilots or POCs?',
    a: 'Starter/Pro include trials; Enterprise can run a scoped pilot with custom SLAs.',
  },
  {
    q: 'How are promos applied?',
    a: 'Automatic promos apply to listed plans; coupon codes stack only when eligible.',
  },
  {
    q: 'What about compliance and audit?',
    a: 'Enterprise includes audit logging, SSO/SAML options, and custom reviews.',
  },
];

const socialProof = [
  'Latency-safe infra',
  'Risk-first execution',
  'Battle-tested in live comps',
  'Trusted by systematic desks',
];

function PricingCard({ plan, period, onCta, onSecondaryCta }) {
  const price = plan.pricing?.[period];
  const isCustom = price?.final === null || price?.final === undefined || Number(price?.final) <= 0;
  const hasDiscount = !isCustom && price?.savings > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        height: '100%',
        position: 'relative',
        borderColor: plan.recommended ? 'primary.main' : 'divider',
        boxShadow: 'var(--glow)',
        background: 'var(--card)',
        border: '1px solid',
        borderColor: plan.recommended ? 'primary.main' : 'var(--border)',
        color: 'var(--text)',
      }}
    >
      <Stack spacing={2} height="100%">
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" color="text.secondary">
              {plan.slug.toUpperCase()}
            </Typography>
            <Typography variant="h5">{plan.name}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {plan.badge && <Chip label={plan.badge} color="secondary" size="small" />}
            {plan.recommended && <Chip label="Recommended" color="primary" size="small" />}
          </Stack>
        </Stack>

        <Stack spacing={0.5} alignItems="center" sx={{ textAlign: 'center' }}>
          {isCustom ? (
            <Typography variant="h5">Get a quote</Typography>
          ) : (
            <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="center">
              <Typography variant="h4">{formatPrice(price?.final, plan.currency)}</Typography>
              {period === 'monthly' ? (
                <Typography variant="body2" color="text.secondary">
                  /mo
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  /yr
                </Typography>
              )}
            </Stack>
          )}
          {hasDiscount && (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                {formatPrice(price?.base, plan.currency)}
              </Typography>
              <Chip
                label={`Save ${price.savings_pct}%`}
                color="success"
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
          <Typography variant="body2" color="text.secondary">
            {plan.description}
          </Typography>
          {plan.usage_notes && (
            <Typography variant="body2" color="text.secondary">
              {plan.usage_notes}
            </Typography>
          )}
        </Stack>

        <Divider />

        <Stack spacing={1}>
          {plan.features?.slice(0, 6).map((feature) => (
            <Stack key={feature} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">•</Typography>
              <Typography variant="body2">{feature}</Typography>
            </Stack>
          ))}
          {plan.features?.length > 6 && (
            <Typography variant="body2" color="text.secondary">
              + {plan.features.length - 6} more
            </Typography>
          )}
        </Stack>

        <Stack spacing={1} sx={{ mt: 'auto' }}>
          {plan.trial_days > 0 && (
            <Chip
              label={`${plan.trial_days}-day trial included`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
          {plan.support && (
            <Typography variant="body2" color="text.secondary">
              Support: {plan.support}
            </Typography>
          )}
          <Button
            variant={plan.recommended ? 'contained' : 'outlined'}
            size="large"
            fullWidth
            onClick={() => onCta(plan)}
          >
            {plan.cta_label || 'Book a demo'}
          </Button>
          <CTAButton
            size="large"
            fullWidth
            onClick={() => onSecondaryCta(plan)}
            sx={{ mt: 1 }}
          >
            Contact sales
          </CTAButton>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PricingComparison({ plans }) {
  const allFeatures = useMemo(() => {
    const uniq = new Set();
    plans.forEach((p) => (p.features || []).forEach((f) => uniq.add(f)));
    return Array.from(uniq);
  }, [plans]);

  if (plans.length === 0 || allFeatures.length === 0) return null;

  return (
    <Box sx={{ mt: 6, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        Compare plans
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxWidth: 1000, mx: 'auto', background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Feature</TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id}>{plan.name}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allFeatures.map((feature) => (
              <TableRow key={feature}>
                <TableCell>{feature}</TableCell>
                {plans.map((plan) => (
                  <TableCell key={plan.id + feature}>
                    {plan.features?.includes(feature) ? '✅' : '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function RLPricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [coupon, setCoupon] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState('');
  const [availableSymbols, setAvailableSymbols] = useState(['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'TRX/USDT', 'XRP/USDT']);
  const [availableTimeframes] = useState(['15m', '1h', '4h', '1d']);
  const [selectedSymbols, setSelectedSymbols] = useState(['BTC/USDT', 'ETH/USDT']);
  const [selectedTimeframes, setSelectedTimeframes] = useState(['1h', '4h']);
  const [calcAggregation, setCalcAggregation] = useState('max');
  const [calcResult, setCalcResult] = useState(null);
  const [calcError, setCalcError] = useState('');
  const [calcLoading, setCalcLoading] = useState(false);
  const [multiPairModel, setMultiPairModel] = useState(true);
  const [totalTimesteps, setTotalTimesteps] = useState('500000');
  const [generations, setGenerations] = useState('10');
  const [portfolios, setPortfolios] = useState('2');
  const [competitions, setCompetitions] = useState('1');
  const [modelsPerCompetition, setModelsPerCompetition] = useState('5');
  const [indicatorCount, setIndicatorCount] = useState('10');
  const [step, setStep] = useState(0);

  const loadPlans = useCallback(
    async (couponCode) => {
      try {
        setLoading(true);
        setError(null);
        const res = await listPricingPlans(couponCode);
        console.info('[pricing] loaded plans', { count: res?.plans?.length, coupon: couponCode, plans: res?.plans });
        setPlans(res?.plans || []);
        setAppliedCoupon(couponCode || '');
      logPricingEvent({
        name: 'pricing_loaded',
        context: { plans: res?.plans?.length || 0, coupon: couponCode || null },
      }).catch(() => {});
      } catch (err) {
        console.error('[pricing] failed to load plans', err);
        setError('Unable to load pricing right now.');
      } finally {
        setLoading(false);
      }
    },
    [setPlans]
  );

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    getAvailableSymbols()
      .then((res) => {
        if (Array.isArray(res)) {
          setAvailableSymbols(res);
        } else if (Array.isArray(res?.symbols)) {
          setAvailableSymbols(res.symbols);
        }
      })
      .catch(() => {});
  }, []);

  const handleApplyCoupon = async () => {
    await loadPlans(coupon.trim() || undefined);
    if (coupon.trim()) {
      logPricingEvent({
        name: 'coupon_applied',
        plan_slug: null,
        context: { coupon: coupon.trim() },
      }).catch(() => {});
    }
  };

  const handleCalc = async () => {
    if (!selectedSymbols.length || !selectedTimeframes.length) {
      setCalcError('Add at least one symbol and one timeframe.');
      return;
    }
    const symbols = selectedSymbols;
    const timeframes = selectedTimeframes;
    setCalcError('');
    setCalcLoading(true);
    try {
      const payload = {
        symbols,
        timeframes,
        aggregation: calcAggregation,
        couponCode: appliedCoupon || coupon.trim() || undefined,
        totalTimesteps: totalTimesteps ? Number(totalTimesteps) : undefined,
        generations: generations ? Number(generations) : undefined,
        multiPairModel,
        portfolios: portfolios ? Number(portfolios) : undefined,
        competitions: competitions ? Number(competitions) : undefined,
        modelsPerCompetition: modelsPerCompetition ? Number(modelsPerCompetition) : undefined,
        indicatorCount: indicatorCount ? Number(indicatorCount) : undefined,
      };
      console.info('[pricing-calc] request', payload);
      const res = await calculatePricing({
        symbols,
        timeframes,
        aggregation: calcAggregation,
        couponCode: appliedCoupon || coupon.trim() || undefined,
        totalTimesteps: totalTimesteps ? Number(totalTimesteps) : undefined,
        generations: generations ? Number(generations) : undefined,
        multiPairModel,
        portfolios: portfolios ? Number(portfolios) : undefined,
        competitions: competitions ? Number(competitions) : undefined,
        modelsPerCompetition: modelsPerCompetition ? Number(modelsPerCompetition) : undefined,
        indicatorCount: indicatorCount ? Number(indicatorCount) : undefined,
      });
      console.info('[pricing-calc] response', res);
      setCalcResult(res);
      logPricingEvent({
        name: 'pricing_calc',
        context: {
          symbols,
          timeframes,
          aggregation: calcAggregation,
          coupon: appliedCoupon || coupon || null,
          total_timesteps: totalTimesteps || null,
          generations: generations || null,
          multi_pair_model: multiPairModel,
          portfolios: portfolios || null,
          competitions: competitions || null,
          models_per_competition: modelsPerCompetition || null,
          indicator_count: indicatorCount || null,
        },
      }).catch(() => {});
    } catch (err) {
      console.error('[pricing-calc] failed', err);
      setCalcError('Unable to calculate pricing right now.');
    } finally {
      setCalcLoading(false);
    }
  };

  const handlePeriodChange = (_, value) => {
    if (value) {
      setPeriod(value);
      logPricingEvent({ name: 'period_switch', context: { period: value } }).catch(() => {});
    }
  };

  const handleCta = (plan, action = 'primary') => {
    logPricingEvent({
      name: 'pricing_cta_click',
      plan_id: plan.id,
      plan_slug: plan.slug,
      context: { action, period, price: plan.pricing?.[period]?.final },
    }).catch(() => {});
    const target = plan.cta_link || '/contact';
    window.location.href = target;
  };

  const handleSecondaryCta = (plan) => handleCta(plan, 'contact');

  const recommended = plans.find((p) => p.recommended) || plans[0];
  const heroCopy = recommended
    ? `Scale with ${recommended.name}: ${recommended.description}`
    : 'Pick the right package for your desk';

  const steps = ['Select pairs', 'Select timeframes', 'Training effort', 'Tooling & calc'];

  const calcInputs = calcResult?.inputs || {};
  const reqPortfolios = Number(calcInputs.portfolios ?? portfolios ?? 0);
  const reqCompetitions = Number(calcInputs.competitions ?? competitions ?? 0);
  const reqModelsPerComp = Number(calcInputs.models_per_competition ?? modelsPerCompetition ?? 0);
  const reqTimesteps = Number(calcInputs.total_timesteps ?? totalTimesteps ?? 0);
  const reqGenerations = Number(calcInputs.generations ?? generations ?? 0);
  const reqIndicators = Number(calcInputs.indicator_count ?? indicatorCount ?? 0);

  const renderStepContent = (idx) => {
    switch (idx) {
      case 0:
        return (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Symbols</Typography>
            <FormGroup row>
              {availableSymbols.map((sym) => (
                <FormControlLabel
                  key={sym}
                  control={
                    <Checkbox
                      checked={selectedSymbols.includes(sym)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedSymbols((prev) => Array.from(new Set([...prev, sym])));
                        } else {
                          setSelectedSymbols((prev) => prev.filter((s) => s !== sym));
                        }
                      }}
                    />
                  }
                  label={sym}
                />
              ))}
            </FormGroup>
          </Stack>
        );
      case 1:
        return (
          <Stack spacing={1}>
            <Typography variant="subtitle2">Timeframes</Typography>
            <FormGroup row>
              {availableTimeframes.map((tf) => (
                <FormControlLabel
                  key={tf}
                  control={
                    <Checkbox
                      checked={selectedTimeframes.includes(tf)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTimeframes((prev) => Array.from(new Set([...prev, tf])));
                        } else {
                          setSelectedTimeframes((prev) => prev.filter((t) => t !== tf));
                        }
                      }}
                    />
                  }
                  label={tf}
                />
              ))}
            </FormGroup>
          </Stack>
        );
      case 2:
        return (
          <Stack spacing={1.25}>
            <Typography variant="subtitle2">Training effort</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Timesteps"
                  type="number"
                  value={totalTimesteps}
                  onChange={(e) => setTotalTimesteps(e.target.value)}
                  helperText="Baseline includes 500,000"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Generations"
                  type="number"
                  value={generations}
                  onChange={(e) => setGenerations(e.target.value)}
                  helperText="Default 10"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Indicators used"
                  type="number"
                  value={indicatorCount}
                  onChange={(e) => setIndicatorCount(e.target.value)}
                  helperText="Default 10"
                />
              </Grid>
            </Grid>
            <FormControlLabel
              control={<Checkbox checked={multiPairModel} onChange={(e) => setMultiPairModel(e.target.checked)} />}
              label="Multi-pair model"
            />
          </Stack>
        );
      case 3:
      default:
        return (
          <Stack spacing={1.25}>
            <Typography variant="subtitle2">Tooling usage</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Portfolios"
                  type="number"
                  value={portfolios}
                  onChange={(e) => setPortfolios(e.target.value)}
                  helperText="Default 2"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Competitions"
                  type="number"
                  value={competitions}
                  onChange={(e) => setCompetitions(e.target.value)}
                  helperText="Default 1"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Models per competition"
                  type="number"
                  value={modelsPerCompetition}
                  onChange={(e) => setModelsPerCompetition(e.target.value)}
                  helperText="Default 5"
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Aggregation
              </Typography>
              <ToggleButtonGroup value={calcAggregation} exclusive size="small" onChange={(_, val) => val && setCalcAggregation(val)}>
                <ToggleButton value="max">Max</ToggleButton>
                <ToggleButton value="average">Average</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" disabled={calcLoading} onClick={() => setStep(Math.max(step - 1, 0))}>
                Back
              </Button>
              <Button variant="contained" onClick={handleCalc} disabled={calcLoading}>
                {calcLoading ? 'Calculating…' : 'Calculate'}
              </Button>
            </Stack>
          </Stack>
        );
    }
  };

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'var(--card)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--glow)',
          textAlign: 'center',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="h4">Pricing & Packages</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
            {heroCopy}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: '100%',
              maxWidth: 900,
              background: 'var(--surface)',
              borderColor: 'var(--border)',
              boxShadow: 'var(--glow)',
            }}
          >
            <Stack spacing={2}>
              <Stepper activeStep={step} alternativeLabel sx={{ width: '100%' }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ p: 2, border: '1px dashed var(--border)', borderRadius: 2, background: 'var(--card)' }}>
                {renderStepContent(step)}
                {step < steps.length - 1 && (
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button variant="outlined" disabled={step === 0} onClick={() => setStep(Math.max(step - 1, 0))}>
                      Back
                    </Button>
                    <Button variant="contained" onClick={() => setStep(Math.min(step + 1, steps.length - 1))}>
                      Next
                    </Button>
                  </Stack>
                )}
              </Box>

              {calcError && (
                <Alert severity="warning" sx={{ m: 0 }}>
                  {calcError}
                </Alert>
              )}
              {calcResult?.plans && calcResult.plans.length > 0 && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)' }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Customized RL Boilerplate</TableCell>
                        <TableCell>Tooling (mo)</TableCell>
                        <TableCell>Tooling (yr)</TableCell>
                        <TableCell align="right">Plan & combos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const p = calcResult.plans[0];
                        const comboCount = p.breakdown?.combo_count || 0;
                        const comboPer = p.breakdown?.combo_surcharge_per_combo || 0;
                        const comboTotalMo = p.combo_surcharge_monthly || 0;
                        const planBaseMo = p.monthly || 0;
                        const planSubtotalMo = planBaseMo + comboTotalMo;
                        return (
                          <TableRow>
                            <TableCell>Tooling for selected pairs/timeframes</TableCell>
                            <TableCell>{formatPrice(p.tooling_monthly)}</TableCell>
                            <TableCell>{formatPrice(p.tooling_annual)}</TableCell>
                            <TableCell align="right">
                              <Stack spacing={0.5} alignItems="flex-end">
                                <Typography variant="body2" color="text.secondary">
                                  agg: {p.aggregation}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Plan: {formatPrice(planBaseMo)} + {Math.max(comboCount - 1, 0)} × {formatPrice(comboPer)} ={' '}
                                  {formatPrice(planSubtotalMo)}
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                  {calcResult.plans[0]?.breakdown && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Cost breakdown
                      </Typography>
                      <Stack spacing={1}>
                        {(() => {
                          const p = calcResult.plans[0];
                          const b = p.breakdown;
                          const inputs = calcResult.inputs || {};
                          const reqPortfolios = Number(inputs.portfolios ?? portfolios ?? 0);
                          const reqCompetitions = Number(inputs.competitions ?? competitions ?? 0);
                          const reqModelsPerComp = Number(inputs.models_per_competition ?? modelsPerCompetition ?? 0);
                          const reqTimesteps = Number(inputs.total_timesteps ?? totalTimesteps ?? 0);
                          const reqGenerations = Number(inputs.generations ?? generations ?? 0);
                          const reqIndicators = Number(inputs.indicator_count ?? indicatorCount ?? 0);
                          const comboCount = b.combo_count || 0;
                          const comboPer = b.combo_surcharge_per_combo || 0;
                          const comboMo = b.combo_surcharge_monthly || 0;
                          const planMo = p.monthly || 0;
                          const planTotal = planMo + comboMo;
                          return (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                Plan base: {formatPrice(planMo)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Combos: {Math.max(comboCount - 1, 0)} × {formatPrice(comboPer)} = {formatPrice(comboMo)}
                              </Typography>
                              <Typography variant="body2">
                                Plan subtotal: {formatPrice(planTotal)} /mo
                              </Typography>
                            </>
                          );
                        })()}
                        <Divider />
                        <Typography variant="body2" color="text.secondary">
                          Portfolios (requested {formatUnit(reqPortfolios)}): {formatTooling(calcResult.plans[0].breakdown.portfolios)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Competitions (requested {formatUnit(reqCompetitions)}): {formatTooling(calcResult.plans[0].breakdown.competitions)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Model seats (models/competition {formatUnit(reqModelsPerComp)}): {formatTooling(calcResult.plans[0].breakdown.model_seats)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Timesteps (requested {formatUnit(reqTimesteps)}): {formatTooling(calcResult.plans[0].breakdown.effort_timesteps)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Generations (requested {formatUnit(reqGenerations)}): {formatTooling(calcResult.plans[0].breakdown.effort_generations)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Indicators (requested {formatUnit(reqIndicators)}): {formatTooling(calcResult.plans[0].breakdown.effort_indicators)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Multi-pair uplift: {formatTooling(calcResult.plans[0].breakdown.effort_markup_multi_pair)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Indicator allowance (included): {formatUnit(calcResult.plans[0].breakdown.indicator_allowance)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Included timesteps: {formatUnit(calcResult.plans[0].breakdown.included_timesteps)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Timestep unit: {formatUnit(calcResult.plans[0].breakdown.timestep_unit)}
                        </Typography>
                        <Typography variant="body2">
                          Tooling (mo): {formatTooling(calcResult.plans[0].breakdown.tooling_monthly)}
                        </Typography>
                        <Typography variant="body2">
                          Tooling (yr): {formatTooling(calcResult.plans[0].breakdown.tooling_annual)}
                        </Typography>
                        <Divider />
                        {(() => {
                          const p = calcResult.plans[0];
                          const b = p.breakdown;
                          const comboMo = b.combo_surcharge_monthly || 0;
                          const planMo = p.monthly || 0;
                          const planTotal = planMo + comboMo;
                          const monthlyTotal = (p.total_monthly ?? planTotal + p.tooling_monthly) || planTotal + (p.tooling_monthly || 0);
                          const annualTotal = p.total_annual || null;
                          return (
                            <>
                              <Typography variant="body2" fontWeight={600}>
                                Total monthly: {formatPrice(monthlyTotal)}
                              </Typography>
                              {annualTotal !== null && (
                                <Typography variant="body2" color="text.secondary">
                                  Total annual: {formatPrice(annualTotal)}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </Stack>
                    </Box>
                  )}
                </TableContainer>
              )}
            </Stack>
          </Paper>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%' }}
          >
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              color="primary"
              sx={{ justifyContent: 'center' }}
            >
              <ToggleButton value="monthly">Monthly</ToggleButton>
              <ToggleButton value="annual">Annual (save vs monthly)</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
              <TextField
                size="small"
                label="Coupon code"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant="outlined" onClick={handleApplyCoupon}>
                Apply
              </Button>
            </Stack>
            {appliedCoupon && (
              <Chip label={`Applied: ${appliedCoupon}`} color="success" variant="outlined" />
            )}
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            {socialProof.map((item) => (
              <Chip key={item} label={item} size="small" variant="outlined" />
            ))}
          </Stack>
        </Stack>
      </Paper>

      {loading && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}>
          <Typography>Loading pricing…</Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={2}>
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <PricingCard plan={plan} period={period} onCta={handleCta} onSecondaryCta={handleSecondaryCta} />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && !error && plans.length === 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}>
          <Typography>No plans published yet.</Typography>
        </Paper>
      )}

      <PricingComparison plans={plans} />

      <Box sx={{ mt: 6 }}>
        <Paper
          variant="outlined"
          sx={{ p: 3, textAlign: 'center', background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
        >
          <Stack spacing={1.5} alignItems="center">
            <Typography variant="h6">Trusted by systematic teams</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 800, textAlign: 'center' }}>
              “The autonomous stack let us ship faster without sacrificing risk controls. Competitions
              surface the best variants while the portfolio guardrails keep us honest.”
            </Typography>
            <Typography variant="body2" color="text.secondary">
              — Quant Lead, Digital Asset Desk
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
              {['AlphaWave Labs', 'Nordic Quant', 'Atlas Markets', 'Helix Digital'].map((logo) => (
                <Chip key={logo} label={logo} variant="outlined" size="small" />
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          FAQs
        </Typography>
        <Grid container spacing={2}>
          {faqItems.map((item) => (
            <Grid item xs={12} md={6} key={item.q}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', textAlign: 'center', background: 'var(--card)', borderColor: 'var(--border)', boxShadow: 'var(--glow)', color: 'var(--text)' }}
              >
                <Typography variant="subtitle1">{item.q}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

